import fs from "node:fs";
import path from "node:path";
import { detectDomains } from "@skaile/workspaces/core";
import { discoverFlows } from "../flow-discovery.ts";
import { aiResourceRoots } from "../paths.ts";
import { findSkills } from "../skill-walker.ts";

/**
 * Return skill names for tab-completion of `skaile skill show <TAB>`.
 *
 * @docLink cli/dev-guide#completion
 */
export function resolveSkillNames(): string[] {
  return findSkills().map((s) => s.name);
}

/**
 * Return skill domain names for tab-completion of `skaile skill list <TAB>`.
 *
 * @docLink cli/dev-guide#completion
 */
export function resolveDomains(): string[] {
  // `detectDomains` returns only real domain folders (those containing
  // skills/flows/agents subdirs), so flat asset-kind dirs like `skills/` or
  // `bundles/` are never offered as bogus domain completions.
  const domains = new Set<string>();
  for (const root of aiResourceRoots()) {
    for (const d of detectDomains(root)) domains.add(d.id);
  }
  return [...domains];
}

/**
 * Return flow IDs for tab-completion of `skaile run <TAB>` and `skaile flow show <TAB>`.
 *
 * @docLink cli/dev-guide#completion
 */
export function resolveFlowIds(): string[] {
  try {
    return discoverFlows().map((f) => f.id);
  } catch {
    return [];
  }
}

/**
 * Return session run-IDs for tab-completion of `skaile session show/switch/delete <TAB>`.
 *
 * Synchronous filesystem walk — completer callers cannot await. Reads filenames
 * from `<projectDir>/.skaile/sessions/*.json` without parsing each session file.
 *
 * @param projectDir - Project directory to scan for sessions.
 * @docLink cli/dev-guide#completion
 */
export function resolveSessionIds(projectDir: string): string[] {
  const sessionsDir = path.join(projectDir, ".skaile", "sessions");
  if (!fs.existsSync(sessionsDir)) return [];
  try {
    return fs
      .readdirSync(sessionsDir)
      .filter((f) => f.endsWith(".json"))
      .map((f) => f.slice(0, -".json".length));
  } catch {
    return [];
  }
}

/**
 * Static list of config key names for tab-completion of `skaile config get/set <TAB>`.
 *
 * @docLink cli/dev-guide#completion
 */
export const CONFIG_KEYS = ["provider", "model", "driver", "framework", "projectDir", "apiKey"];
